import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import styles from '../searchView.module.scss';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import Pagination from 'v2/common/Pagination/Pagination';
import {
  FetchSearchesParams,
  GenericObject,
  SearchEntity,
  SearchStateProps,
} from 'v2/models';
import { REVIEW_STATUS } from 'v2/utils/searchUtils';
import {
  Columns,
  searchViewPath,
  TypeOfColumn,
} from 'v2/utils/searchViewUtils';
import useSelectUnreadProposals from 'v2/hooks/useSelectUnreadProposals';
import useFetchSearchesPaginated from 'v2/hooks/useFetchSearchesPaginated';
import useGetPageNumber, {
  useGetPageNumberProps,
} from 'v2/hooks/useGetPageNumber';
import SearchList from 'v2/common/SearchList/SearchList';
import SearchHeader from 'v2/common/SearchHeader/SearchHeader';
import { SearchItem } from 'v2/common/SearchList/SearchItem/SearchItem';
import SearchSpinner from 'v2/common/SearchViewComponents/SearchSpinner';
import NoSearchesMessage from 'v2/common/SearchViewComponents/NoSearchesMessage';

const InProgres = ({ user, unreadMessages }: SearchStateProps) => {
  const pageParams: useGetPageNumberProps = {
    onNotValidPageNumber: changePage,
  };

  const history = useHistory();
  const page = useGetPageNumber(pageParams);
  const pageTitle = 'My Requests';
  const columns: Columns = [
    {
      type: TypeOfColumn.title,
      label: 'Title',
    },
    {
      type: TypeOfColumn.originator,
      label: 'Originator',
      disabled: !!user.single_person_organization,
    },
    {
      type: TypeOfColumn.reviewer,
      label: 'Reviewer',
    },
    {
      type: TypeOfColumn.searchType,
      label: 'Request Type',
    },
    {
      type: TypeOfColumn.responses,
      label: 'Responses',
      responsesCount: useSelectUnreadProposals(),
    },
    {
      type: TypeOfColumn.conversations,
      label: 'Conversations',
      conversationCount: unreadMessages,
    },
    {
      type: TypeOfColumn.dateCreated,
      label: 'Date Created',
    },
  ];

  const inProgressSearchesParams: FetchSearchesParams = {
    review_status__ne: REVIEW_STATUS.UNDER_REVIEW,
    active: true,
    depth: 'organization',
    page,
  };

  const {
    isFetching: inProgressSearchesFetching,
    data: { results: inProgressSearches, count: inProgressSearchesCount } = {},
    isError: inProgressSearchesIsError,
    error,
  } = useFetchSearchesPaginated(inProgressSearchesParams, [
    'inProgressSearchesList',
  ]);

  function changePage(page: number) {
    history.push(`${searchViewPath.inProgress}/${page}`);
    window.scrollTo(0, 0);
  }

  if (inProgressSearchesIsError) {
    if (error === 'Invalid page.') {
      changePage(1);
    }
    return <div data-testid="errorFetching"></div>;
  }

  const renderSearchList = () => {
    if (inProgressSearches) {
      return (
        <>
          <SearchList
            columns={columns}
            data={inProgressSearches}
            renderSearchItem={(search: SearchEntity, columns: Columns) => {
              return (
                <SearchItem columns={columns} search={search} key={search.id} />
              );
            }}
          />
          <Pagination
            totalCountOfItems={inProgressSearchesCount}
            callBack={(pageSelected) => {
              changePage(pageSelected);
            }}
            currentPageOfItems={page}
          />
        </>
      );
    }
  };

  const renderInProgessSearches = () => {
    if (inProgressSearchesFetching) {
      return <SearchSpinner />;
    } else if (!inProgressSearches || !inProgressSearches?.length) {
      return <NoSearchesMessage />;
    } else {
      return renderSearchList();
    }
  };

  return (
    <div className={classnames('fix-bg-images', styles.searchViewContainer)}>
      <BackgroundImages />
      <span aria-level={1}>
        <SearchHeader pageTitle={pageTitle} />
      </span>
      <div className="container-xl">{renderInProgessSearches()}</div>
    </div>
  );
};

const mapStateToProps = (state: GenericObject): SearchStateProps => {
  return {
    user: state.user.user,
    unreadMessages: state.chat.unreadMessages,
  };
};

export default connect<SearchStateProps>(mapStateToProps)(InProgres);
